'use client';

import { useState } from 'react';
import { mockAnalyticsData } from '@/features/analytics-negociacoes/lib/mockData';
import type { GeneralDashboard, QualitativeDashboard } from '@/features/negociacoes';
import { getEngagementCases } from '@/features/negociacoes/store/actions';
import { FaixasValorChart } from '@/features/analytics-negociacoes/components/FaixasValorChart';
import { RecomendacoesList } from '@/features/analytics-negociacoes/components/RecomendacoesList';
import dynamic from 'next/dynamic';
import { LazyLoader } from '@/shared/components/ui';
import { Button } from '@/shared/components/ui/common';
import { useToast } from '@/shared/providers';
import { useAppDispatch } from '@/store/hooks';
import { FeatherDownload, FeatherRefreshCw } from '@subframe/core';
import { VisaoGeralTab } from './VisaoGeralTab';
const AnaliseQualitativa = dynamic(() => import('@/features/analytics-negociacoes/components/AnaliseQualitativa').then(m => m.AnaliseQualitativa), {
  loading: () => <LazyLoader />,
  ssr: false
});
interface NegociacoesDashboardViewProps {
  dashboardTab: string;
  generalDashboardData: GeneralDashboard | null;
  qualitativeDashboardData: QualitativeDashboard | null;
  isLoadingGeneral?: boolean;
  isLoadingQualitative?: boolean;
  onForceRefreshQualitative?: () => void;
}
export function NegociacoesDashboardView({
  dashboardTab,
  generalDashboardData,
  qualitativeDashboardData,
  isLoadingGeneral,
  isLoadingQualitative,
  onForceRefreshQualitative
}: NegociacoesDashboardViewProps) {
  const dispatch = useAppDispatch();
  const {
    showError
  } = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const handleExportQualitativo = async () => {
    if (!qualitativeDashboardData || isExporting) return;
    setIsExporting(true);
    try {
      const {
        exportQualitativoXlsx
      } = await import('@/features/analytics-negociacoes/utils/exportQualitativoXlsx');
      await exportQualitativoXlsx({
        funnel: generalDashboardData?.funnel ?? null,
        qualitative: qualitativeDashboardData,
        fetchCases: (categoria, page, limit) => dispatch(getEngagementCases({
          categoria,
          page,
          limit
        })).unwrap()
      });
    } catch (error) {
      showError('Erro', 'Falha ao exportar o relatório - ' + (error instanceof Error ? error.message : 'erro desconhecido'));
    } finally {
      setIsExporting(false);
    }
  };
  return <div className="w-full space-y-6">
      {dashboardTab === 'Overview' && (isLoadingGeneral ? <LazyLoader /> : generalDashboardData && <VisaoGeralTab generalDashboardData={generalDashboardData} />)}

      {dashboardTab === 'Financial' && <FaixasValorChart data={mockAnalyticsData.valores} />}

      {dashboardTab === 'Qualitative' && (isLoadingQualitative ? <LazyLoader /> : qualitativeDashboardData && <section>
              <h2 className="mb-6 text-xl font-bold text-neutral-900">
                Análise Qualitativa e IA
                <br />
                <span className="text-body font-body text-subtext-color flex items-center gap-2">
                  Última atualização em{' '}
                  {new Date(qualitativeDashboardData.responseText.lastUpdateAt).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
                  {onForceRefreshQualitative && <Button type="button" variant="neutral-tertiary" size="small" icon={<FeatherRefreshCw className="h-3.5 w-3.5" />} onClick={onForceRefreshQualitative} disabled={isLoadingQualitative}>
                      Atualizar
                    </Button>}
                  <Button type="button" variant="neutral-secondary" size="small" icon={<FeatherDownload className="h-3.5 w-3.5" />} onClick={handleExportQualitativo} loading={isExporting} disabled={isExporting}>
                    Exportar
                  </Button>
                </span>
              </h2>
              <AnaliseQualitativa qualitativeDashboardData={qualitativeDashboardData} />
            </section>)}

      {dashboardTab === 'Insights' && <div className="h-full rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
          <RecomendacoesList data={mockAnalyticsData.recomendacoes} />
        </div>}
    </div>;
}